import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/modules/Auth/store/index'
import {
  canAccess,
  getFirstAccessiblePath,
  getRouteAllowedRoles,
} from '@/modules/Core/utils/authorization'

function redirectToFirstAccessible(
  to: RouteLocationNormalized,
  role: ReturnType<typeof useAuthStore>['role'],
  next: NavigationGuardNext
) {
  const path = getFirstAccessiblePath(role)

  if (path) {
    next(path)
    return
  }

  const authStore = useAuthStore()
  authStore.logout()

  if (to.name === 'login') {
    next()
    return
  }

  next({ name: 'login' })
}

export function authGuard(
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore()

  if (to.name !== 'login' && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    redirectToFirstAccessible(to, authStore.role, next)
    return
  }

  if (!authStore.isAuthenticated) {
    next()
    return
  }

  if (to.name === 'home') {
    redirectToFirstAccessible(to, authStore.role, next)
    return
  }

  const allowedRoles = getRouteAllowedRoles(to.matched)

  if (allowedRoles && !canAccess(authStore.role, allowedRoles)) {
    redirectToFirstAccessible(to, authStore.role, next)
    return
  }

  next()
}
